import React, { useEffect, useRef, useState } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { InventoryApiService } from "../../Service/InventoryApiService";
import { Service } from "../../Service/ApiService";
import DataGridLoader from '../Common/DataGridLoader';
import { Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import { CommonService } from "../../Service/CommonService";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DateRange } from "react-date-range";
import Dropdown from "react-bootstrap/Dropdown";
import MultiSelectChipInput from "../Common/MultiSelectChipInput";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Accordion } from "react-bootstrap";

const CompanyBillingActivityReport: React.FC = () => {
    const previousMonthRange = CommonService.getPreviousMonthDateRange();
    const [companiesList, setCompaniesList] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [sortField, setSortField] = useState("companyName"); // Default sort
    const [sortDirection, setSortDirection] = useState("asc"); // Default ascending
    const controllerRef = useRef<AbortController | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const datePickerRef = useRef<HTMLDivElement | null>(null);
    const mainInputRef = useRef<HTMLDivElement | null>(null);
    const [mainPickerStyle, setMainPickerStyle] = useState<React.CSSProperties>({});
    const [isMainDatePickerOpen, setIsMainDatePickerOpen] = useState(false);
    const [filter, setFilter] = useState<any>(() => ({
        companyName: null,
        startDate: previousMonthRange.startDateText,
        endDate: previousMonthRange.endDateText
    }));
    // final selected values
    const [selectedDateRange, setSelectedDateRange] = useState<any>({
        startDate: filter.startDate ?? null,
        endDate: filter.endDate ?? null,
        key: "selection",
    });
    // temp values for picker
    const [tempRange, setTempRange] = useState<any>({
        startDate: filter.startDate ?? null,
        endDate: filter.endDate ?? null,
        key: "selection",
    });
    const isFilterApplied = Object.values(filter).some(
        (value) => value !== null && value !== "" && value !== undefined
    );
    const localeText = {
        noRowsLabel: isLoading ? "Fetching records ..." : "No Record Found",
    };

    const [dateRangeError, setDateRangeError] = useState<string | null>(null);
    const [allCompanyOptions, setAllCompanyOptions] = useState<string[]>([]);
    const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
    const companyOptionsControllerRef = useRef<AbortController | null>(null);

    const isDateRangeValid = (start: string | null, end: string | null): boolean => {
        if (!start || !end) return true;
        const s = new Date(start + "T00:00:00");
        const e = new Date(end + "T00:00:00");
        const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
        return months < 12;
    };

    useEffect(() => {
        if (companyOptionsControllerRef.current) companyOptionsControllerRef.current.abort();
        const controller = new AbortController();
        companyOptionsControllerRef.current = controller;
        Service.fetchCompaniesList()
            .then((res: any) => {
                const names: string[] = (res.data || []).map((c: any) => c.companyName).filter(Boolean).sort();
                setAllCompanyOptions(names);
            })
            .catch(() => {});
    }, []);
    const formatDate = (date: Date | null) => {
        if (!date) return null;

        const parsedDate =
            typeof date === "string"
                ? new Date(date + "T00:00:00")
                : date;

        return parsedDate.toLocaleDateString("en-CA");
    };

    useEffect(() => {
        getCompanyBillingActivity(page, pageSize, filter, sortField, sortDirection);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            // MAIN PICKER
            if (
                isMainDatePickerOpen &&
                datePickerRef.current &&
                !datePickerRef.current.contains(target) &&
                mainInputRef.current &&
                !mainInputRef.current.contains(target)
            ) {
                setIsMainDatePickerOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isMainDatePickerOpen]);


    useEffect(() => {
        const handleScroll = () => {
            updatePickerPositions();
        };

        const handleResize = () => {
            updatePickerPositions();
        };

        window.addEventListener("scroll", handleScroll, true); // 👈 important: true for nested scrolls
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("scroll", handleScroll, true);
            window.removeEventListener("resize", handleResize);
        };
    }, [isMainDatePickerOpen]);

    const getFilterPanelPosition = (
        triggerEl: HTMLElement | null,
        panelWidth: number = 350,
        panelHeight: number = 500,
        gap: number = 8
    ) => {
        if (!triggerEl) return {};

        const rect = triggerEl.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let left = rect.left;
        let top = rect.bottom + gap;

        // 👉 Try normal position first
        // If not enough space below → move above
        if (top + panelHeight > viewportHeight) {
            top = rect.top - panelHeight - gap;
        }

        // 👉 FINAL CLAMP (this is the key part 🔥)

        // Horizontal clamp
        left = Math.max(16, Math.min(left, viewportWidth - panelWidth - 16));

        // Vertical clamp
        top = Math.max(16, Math.min(top, viewportHeight - panelHeight - 16));

        return {
            position: "fixed" as const,
            top: `${top}px`,
            left: `${left}px`,
            zIndex: 9999,
        };
    };

    //add tooltip
    const CellWithTooltip = ({ value }: { value: string | number }) => {
        return (
            <Tooltip arrow title={value?.toString()}>
                <span>{value?.toString()}</span>
            </Tooltip>
        );
    };

    const getCompanyBillingActivity = async (page, size, filter: any, sortField: any, sortDirection: any) => {

        if (!isDateRangeValid(filter?.startDate, filter?.endDate)) {
            setDateRangeError("Date range cannot exceed 12 months.");
            return;
        }
        setDateRangeError(null);

        const controller = new AbortController();
        const signal = controller.signal;
        try {
            setIsLoading(true);
            setCompaniesList([]);
            // Cancel the previous request if any
            if (controllerRef.current) {
                controllerRef.current.abort();
            }

            // Store the current controller to cancel the request if needed
            controllerRef.current = controller;

            const response: any = await InventoryApiService.getCompanyBillingActivity(page, size, filter, sortField, sortDirection, signal);
            if (response?.data?.content !== undefined) {
                setCompaniesList(response.data.content);
                setTotalCount(response.data.totalElements);
            } else if (typeof response?.data === "string") {
                setDateRangeError(response.data);
            }
            setIsLoading(false);
        } catch (error: any) {
            const msg = error?.response?.data;
            if (typeof msg === "string" && msg.toLowerCase().includes("date range")) {
                setDateRangeError(msg);
            } else {
                console.error("Error fetching data:", error);
            }
            setIsLoading(false);
        }
    }

    const exportCompanyBillingActivity = async (filter: any, sortField: any, sortDirection: any) => {
        const controller = new AbortController();
        const signal = controller.signal;

        try {
            setIsLoading(true);

            // Cancel the previous request if any
            if (controllerRef.current) {
                controllerRef.current.abort();
            }

            // Store the current controller to cancel the request if needed
            controllerRef.current = controller;
            InventoryApiService.exportCompanyBillingActivity(filter, sortField, sortDirection, signal);
            CommonService.Toast.fire({
                title: "Your export is in progress. We'll notify you when it's ready!",
                icon: "success",
            });
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setIsLoading(false);
        }
    };

    const tableDataWithIds = companiesList.map((rowData, index) => ({
        ...rowData,
    }));

    const handleSortChange = (sortModel) => {
        if (sortModel.length > 0) {
            const newSortField = sortModel[0].field;
            const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid

            setSortField(newSortField);
            setSortDirection(newSortDirection);

            getCompanyBillingActivity(page, pageSize, filter, newSortField, newSortDirection);
        }
    };

    const onPaginationModelChange = (newPaginationModel) => {
        setPage(newPaginationModel.page);
        setPageSize(newPaginationModel.pageSize);

        getCompanyBillingActivity(newPaginationModel.page, newPaginationModel.pageSize, filter, sortField, sortDirection);
    };

    const handleFilterReset = (field: keyof any) => {
        const tempFilter = {
            ...filter,
            [field]: null
        };
        if (field === 'companyName') setSelectedCompanies([]);
        setFilter(tempFilter);
    };

    const handleFilterChange = (event: any, field: keyof any) => {
        let value;
        if (field === 'companyName')
            value = event.join(",");
        else
            value = event.target.value;

        const tempFilter = {
            ...filter,
            [field]: value
        };

        // Update the filter state
        setFilter(tempFilter);
    };

    const handleClear = () => {
        handleMainClear();
        const tempFilter = {
            companyName: null,
            startDate: null,
            endDate: null
        };

        setSelectedCompanies([]);
        setDateRangeError(null);
        setFilter(tempFilter);
        getCompanyBillingActivity(page, pageSize, tempFilter, sortField, sortDirection);
    }

    const handleApply = () => {
        const merged = {
            ...filter,
            companyName: selectedCompanies.length > 0 ? selectedCompanies.join(",") : null,
        };
        setFilter(merged);
        getCompanyBillingActivity(page, pageSize, merged, sortField, sortDirection);
    };

    // main date picker actions
    const handleMainClear = () => {
        setTempRange({
            startDate: null,
            endDate: null,
            key: "selection",
        });
        setSelectedDateRange({
            startDate: null,
            endDate: null,
            key: "selection",
        });
        setIsMainDatePickerOpen(false);

        const tempFilter = {
            ...filter,
            startDate: null,
            endDate: null,
        };

        setFilter(tempFilter);
    };

    const getFixedPickerPosition = (
        triggerEl: HTMLDivElement | null,
        pickerWidth: number = 700,
        gap: number = 8
    ) => {
        if (!triggerEl) return {};

        const rect = triggerEl.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        let left = rect.left;
        let top = rect.bottom + gap;

        if (left + pickerWidth > viewportWidth - 16) {
            left = viewportWidth - pickerWidth - 16;
        }

        if (left < 16) {
            left = 16;
        }

        return {
            position: "fixed" as const,
            top: `${top}px`,
            left: `${left}px`,
            zIndex: 9999,
        };
    };

    const handleMainPickerToggle = () => {
        if (!isMainDatePickerOpen) {
            setMainPickerStyle(getFixedPickerPosition(mainInputRef.current, 700));
        }
        setIsMainDatePickerOpen((prev) => !prev);
    };

    const updatePickerPositions = () => {
        if (isMainDatePickerOpen) {
            setMainPickerStyle(
                getFixedPickerPosition(mainInputRef.current, 700)
            );
        }
    }

    // main date picker change
    const handleTempChange = (start: Date | null, end: Date | null) => {
        setTempRange({
            startDate: start,
            endDate: end,
            key: "selection",
        });
    };

    const handleMainCancel = () => {
        setTempRange(selectedDateRange);
        setIsMainDatePickerOpen(false);
    };

    const handleMainOk = () => {
        setSelectedDateRange(tempRange);
        setIsMainDatePickerOpen(false);

        const tempFilter = {
            ...filter,
            startDate: formatDate(tempRange.startDate),
            endDate: formatDate(tempRange.endDate),
        };

        setFilter(tempFilter);
    };

    const columns: GridColDef[] = [
        {
            field: "companyName",
            headerName: "Company Name",
            flex: 1,
            minWidth: 200,
            sortable: true,
            valueGetter: (value: any, row: any) => `${row.companyName ?? ""}`,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            renderCell: (params: any) => (
                <CellWithTooltip value={params.row.companyName} />
            ),
        },
        {
            field: "beginningInventoryCount",
            headerName: "Beginning Inventory Count",
            flex: 1,
            minWidth: 200,
            sortable: true,
            valueGetter: (value: any, row: any) => `${row.beginningInventoryCount ?? ""}`,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            renderCell: (params: any) => (
                <CellWithTooltip value={params.value || "-"} />
            ),
        },
        {
            field: "deliveries",
            headerName: "Deliveries",
            flex: 1,
            minWidth: 200,
            sortable: true,
            valueGetter: (value: any, row: any) => `${row.deliveries ?? ""}`,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            cellClassName: "status-badge",
            renderCell: (params) => {
                return (
                    <div>
                        <CellWithTooltip value={params.value || "-"} />
                        <Dropdown drop="end" className="d-inline-block report-dropdown">
                            <Dropdown.Toggle
                                variant="basic"
                                id="dropdown-basic"
                                className="p-0 ps-1 border-0 outline-0"
                                style={{ marginTop: "-5px" }}
                            >
                                <InfoIcon sx={{ color: "#6F6F6F" }} />
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                <Dropdown.Item href="#/action-1">
                                    <p className="key">Barrel{" "}</p>
                                    <p className="value">{params.row.deliveriesBarrel}</p>
                                </Dropdown.Item>
                                <Dropdown.Item href="#/action-2">
                                    <p className="key">Tote </p>
                                    <p className="value">{params.row.deliveriesTote ?? ""}</p>
                                </Dropdown.Item>
                                <Dropdown.Item href="#/action-3">
                                    <p className="key">Drum </p>
                                    <p className="value">{params.row.deliveriesDrum ?? ""}</p>
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                );
            },
        },
        {
            field: "shipments",
            headerName: "Shipments",
            flex: 1,
            minWidth: 200,
            sortable: true,
            valueGetter: (value: any, row: any) => `${row.shipments ?? ""}`,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            cellClassName: "status-badge",
            renderCell: (params) => {
                return (
                    <div>
                        <CellWithTooltip value={params.value || "-"} />
                        <Dropdown drop="end" className="d-inline-block report-dropdown">
                            <Dropdown.Toggle
                                variant="basic"
                                id="dropdown-basic"
                                className="p-0 ps-1 border-0 outline-0"
                                style={{ marginTop: "-5px" }}
                            >
                                <InfoIcon sx={{ color: "#6F6F6F" }} />
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                <Dropdown.Item href="#/action-1">
                                    <p className="key">Barrel{" "}</p>
                                    <p className="value">{params.row.shipmentsBarrel}</p>
                                </Dropdown.Item>
                                <Dropdown.Item href="#/action-2">
                                    <p className="key">Tote </p>
                                    <p className="value">{params.row.shipmentsTote ?? ""}</p>
                                </Dropdown.Item>
                                <Dropdown.Item href="#/action-3">
                                    <p className="key">Drum </p>
                                    <p className="value">{params.row.shipmentsDrum ?? ""}</p>
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                );
            },
        },
        {
            field: "transferIn",
            headerName: "Transfer In",
            flex: 1,
            minWidth: 200,
            sortable: true,
            valueGetter: (value: any, row: any) => `${row.transferIn ?? ""}`,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            cellClassName: "status-badge",
            renderCell: (params) => {
                return (
                    <div>
                        <CellWithTooltip value={params.value || "-"} />
                        <Dropdown drop="end" className="d-inline-block report-dropdown">
                            <Dropdown.Toggle
                                variant="basic"
                                id="dropdown-basic"
                                className="p-0 ps-1 border-0 outline-0"
                                style={{ marginTop: "-5px" }}
                            >
                                <InfoIcon sx={{ color: "#6F6F6F" }} />
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                <Dropdown.Item href="#/action-1">
                                    <p className="key">Barrel{" "}</p>
                                    <p className="value">{params.row.transferInBarrel}</p>
                                </Dropdown.Item>
                                <Dropdown.Item href="#/action-2">
                                    <p className="key">Tote</p> 
                                    <p className="value">{params.row.transferInTote ?? ""}</p>
                                </Dropdown.Item>
                                <Dropdown.Item href="#/action-3">
                                    <p className="key">Drum</p> 
                                    <p className="value">{params.row.transferInDrum ?? ""}</p>
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                );
            },
        },
        {
            field: "transferOut",
            headerName: "Transfer Out",
            flex: 1,
            minWidth: 200,
            sortable: true,
            valueGetter: (value: any, row: any) => `${row.transferOut ?? ""}`,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            cellClassName: "status-badge",
            renderCell: (params) => {
                return (
                    <div>
                        <CellWithTooltip value={params.value || "-"} />
                        <Dropdown drop="end" className="d-inline-block report-dropdown">
                            <Dropdown.Toggle
                                variant="basic"
                                id="dropdown-basic"
                                className="p-0 ps-1 border-0 outline-0"
                                style={{ marginTop: "-5px" }}
                            >
                                <InfoIcon sx={{ color: "#6F6F6F" }} />
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                <Dropdown.Item href="#/action-1">
                                    <p className="key"> Barrel{" "}</p>
                                    <p className="value">{params.row.transferOutBarrel}</p>
                                </Dropdown.Item>
                                <Dropdown.Item href="#/action-2">
                                    <p className="key">Tote</p> 
                                    <p className="value">{params.row.transferOutTote ?? ""}</p>
                                </Dropdown.Item>
                                <Dropdown.Item href="#/action-3">
                                    <p className="key">Drum</p> 
                                    <p className="value">{params.row.transferOutDrum ?? ""}</p>
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                );
            },
        },
        {
            field: "endingInventoryCount",
            headerName: "Ending Inventory Count",
            flex: 1,
            minWidth: 200,
            sortable: true,
            valueGetter: (value: any, row: any) => `${row.endingInventoryCount ?? ""}`,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            renderCell: (params: any) => (
                <CellWithTooltip value={params.value || "-"} />
            ),
        }
    ];

    return (<>
        <div className="reporting-filter-btn-wrapper">
            {companiesList.length > 0 || isLoading ?
                <>
                    <button title="Export to Excel"
                        className="primary-btn d-inline-flex align-items-center py-1 export-btn"
                        onClick={(e) => {
                            e.currentTarget.blur(); // Removes focus after clicking
                            exportCompanyBillingActivity(filter, sortField, sortDirection);
                        }}
                    >
                        <FileDownloadOutlinedIcon sx={{ marginTop: "5.5px" }} />
                        Export
                    </button>
                </>
                : null}
        </div>
        {dateRangeError && (
            <div className="alert barrel-alert alert-dismissible fade show mx-3 mt-2 mb-0" role="alert">
                <span className="d-flex align-items-center gap-2"><WarningIcon />{dateRangeError}</span>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setDateRangeError(null)}><CloseIcon /></button>
            </div>
        )}
        <div className="user-table-container report-module">
                <Accordion defaultActiveKey="0" className="filter-accordion">
                    <Accordion.Item eventKey="0" className="filter-accordion-item">
                        <Accordion.Header className="filter-accordion-header">
                            <span className="accordion-filter-title">Filters</span>
                            <button
                                className="filter-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClear();
                                }}
                            >
                                Reset All
                            </button>
                        </Accordion.Header>
                        <Accordion.Body className="filter-accordion-body p-0">
                            <div className="filter-panel-accordion">

                                <div className="filter-body">
                                    {/* Date range */}
                                    <div className="filter-section position-relative">
                                        <div className="section-header">
                                            <h4>Date range</h4>
                                            <button className="section-btn" type="button" onClick={handleMainClear}>Reset</button>
                                        </div>
                                        {/* <label htmlFor="dateRange">Filter by Date Range</label> */}
                                        <div className="date-input-wrapper" ref={mainInputRef}>
                                            <input
                                                type="text"
                                                placeholder="Select date range"
                                                readOnly
                                                className="form-control"
                                                value={
                                                    selectedDateRange.startDate && selectedDateRange.endDate
                                                        ? `${formatDate(selectedDateRange.startDate)} - ${formatDate(selectedDateRange.endDate)}`
                                                        : ""
                                                }
                                                onClick={handleMainPickerToggle}
                                            />
                                            <svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M13.5 1.51172H12.7266V0H11.25V1.51172H3.72656V0H2.25V1.51172H1.47656C0.667969 1.51172 0 2.17969 0 3.02344V15.0117C0 15.8203 0.667969 16.5234 1.47656 16.5234H13.5C14.3086 16.5234 14.9766 15.8203 14.9766 15.0117V3.02344C14.9766 2.17969 14.3086 1.51172 13.5 1.51172ZM13.5 15.0117H1.47656V6.75H13.5V15.0117ZM13.5 5.27344H1.47656V3.02344H13.5V5.27344Z" fill="#9CA3AF" />
                                            </svg>
                                        </div>

                                        {isMainDatePickerOpen && (
                                            <div className="datepicker-menu" ref={datePickerRef} style={mainPickerStyle}>
                                                <DateRange
                                                    ranges={[tempRange]}
                                                    editableDateInputs={true}
                                                    dropdownMode="scroll"
                                                    onChange={(item: any) =>
                                                        handleTempChange(
                                                            item.selection.startDate ?? null,
                                                            item.selection.endDate ?? null
                                                        )
                                                    }
                                                    moveRangeOnFirstSelection={false}
                                                    months={2}
                                                    direction="horizontal"
                                                    showDateDisplay={false}
                                                />
                                                <div className="datepicker-menu-bottom">
                                                    <button type="button" onClick={handleMainCancel} className="datepicker-btn">
                                                        CANCEL
                                                    </button>
                                                    <button type="button" onClick={handleMainOk} className="datepicker-btn">
                                                        OK
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Company */}
                                    <div className="filter-section">
                                        <div className="section-header">
                                            <h4>Company</h4>
                                            <button className="section-btn" type="button" onClick={() => handleFilterReset("companyName")}>Reset</button>
                                        </div>
                                        <MultiSelectChipInput
                                            selected={selectedCompanies}
                                            options={allCompanyOptions}
                                            placeholder="Select companies"
                                            searchPlaceholder="Search company"
                                            onToggle={val => setSelectedCompanies(prev =>
                                                prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
                                            )}
                                            onSelectAll={() => setSelectedCompanies(prev =>
                                                prev.length === allCompanyOptions.length ? [] : [...allCompanyOptions]
                                            )}
                                            onReset={() => setSelectedCompanies([])}
                                        />
                                    </div>
                                </div>
                                <div className="filter-bottom">
                                    <div className="filter-actions">
                                        <button className="filter-btn" onClick={handleApply}><CheckIcon /> Apply</button>
                                    </div>
                                </div>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            <div className="userTable">
                <DataGrid
                    rows={tableDataWithIds}
                    columns={columns}
                    className="data-grid"
                    pagination
                    paginationModel={{ page, pageSize }}
                    onPaginationModelChange={onPaginationModelChange}
                    rowCount={totalCount}
                    paginationMode="server"
                    sortingMode="server"
                    onSortModelChange={handleSortChange}
                    disableRowSelectionOnClick={true}
                    pageSizeOptions={[10, 20, 50, 100]}
                    localeText={localeText}
                    getRowId={(row) => row.id}
                    loading={isLoading}
                    slots={{
                        loadingOverlay: DataGridLoader,
                    }}
                    disableColumnResize
                    sx={{
                        "& .MuiDataGrid-columnHeaderCheckbox": {
                            borderBottom: "unset !important"
                        },
                    }}
                />
            </div>
        </div>

    </>
    );
}

export default CompanyBillingActivityReport;